import React, { useState } from 'react';
import { InfoIcon, XIcon, BuildingIcon, PlusCircleIcon } from 'lucide-react';
interface Step2ProgramsUniversitiesProps {
  formData: any;
  updateFormData: (data: any) => void;
}
const Step2ProgramsUniversities: React.FC<Step2ProgramsUniversitiesProps> = ({
  formData,
  updateFormData
}) => {
  // Sample universities list - in a real app, this would come from an API
  const eligibleUniversities = [{
    id: 'uae1',
    name: 'UAE University'
  }, {
    id: 'uae2',
    name: 'Zayed University'
  }, {
    id: 'uae3',
    name: 'Higher Colleges of Technology'
  }, {
    id: 'uae4',
    name: 'Khalifa University'
  }, {
    id: 'uae5',
    name: 'American University of Sharjah'
  }, {
    id: 'uae6',
    name: 'University of Sharjah'
  }, {
    id: 'uae7',
    name: 'Abu Dhabi University'
  }, {
    id: 'uae8',
    name: 'Ajman University'
  }, {
    id: 'uae9',
    name: 'American University in Dubai'
  }, {
    id: 'uae10',
    name: 'Rochester Institute of Technology - Dubai'
  }];
  const [universitySearch, setUniversitySearch] = useState('');
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    updateFormData({
      [name]: value
    });
  };
  const handleUniversitySelect = (universityId: string) => {
    const university = eligibleUniversities.find(u => u.id === universityId);
    if (university && !formData.preferredUniversities.some((u: any) => u.id === universityId)) {
      updateFormData({
        preferredUniversities: [...formData.preferredUniversities, university]
      });
      setUniversitySearch('');
    }
  };
  const handleRemoveUniversity = (universityId: string) => {
    updateFormData({
      preferredUniversities: formData.preferredUniversities.filter((u: any) => u.id !== universityId)
    });
  };
  // Filter universities based on search
  const filteredUniversities = universitySearch ? eligibleUniversities.filter(u => u.name.toLowerCase().includes(universitySearch.toLowerCase()) && !formData.preferredUniversities.some((selected: any) => selected.id === u.id)) : [];
  return <div data-id="element-853">
      <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-854">
        Programs & Universities
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-855">
        Specify the program details and preferred universities for this
        scholarship.
      </p>
      <div className="space-y-6" data-id="element-856">
        <div data-id="element-857">
          <label htmlFor="programName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-858">
            Program Name <span className="text-red-500" data-id="element-859">*</span>
          </label>
          <input type="text" id="programName" name="programName" value={formData.programName || ''} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="e.g., Master of Science in Artificial Intelligence" required data-id="element-860" />
          <p className="text-xs text-gray-500 mt-1" data-id="element-861">
            Enter international program name (not in UAE database)
          </p>
        </div>
        <div data-id="element-862">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-863">
            Preferred Universities (Optional)
          </label>
          <p className="text-sm text-gray-500 mb-3" data-id="element-864">
            Select universities from MoHESR's eligible list where students can
            use this scholarship
          </p>
          {/* Search input */}
          <div className="relative mb-4" data-id="element-865">
            <input type="text" value={universitySearch} onChange={e => setUniversitySearch(e.target.value)} placeholder="Search for universities..." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-866" />
            {/* Dropdown results */}
            {universitySearch && <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto" data-id="element-867">
                {filteredUniversities.length > 0 ? filteredUniversities.map(university => <div key={university.id} className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center" onClick={() => handleUniversitySelect(university.id)} data-id="element-868">
                      <BuildingIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-869" />
                      {university.name}
                    </div>) : <div className="px-4 py-2 text-gray-500" data-id="element-870">
                    No matching universities found
                  </div>}
              </div>}
          </div>
          {/* Selected universities */}
          <div className="flex flex-wrap gap-2 mb-2" data-id="element-871">
            {formData.preferredUniversities.length > 0 ? formData.preferredUniversities.map((university: any) => <div key={university.id} className="bg-primary-50 text-primary-700 px-3 py-1.5 rounded-full text-sm flex items-center" data-id="element-872">
                  <BuildingIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-873" />
                  {university.name}
                  <button type="button" onClick={() => handleRemoveUniversity(university.id)} className="ml-1.5 text-primary-500 hover:text-primary-700" data-id="element-874">
                    <XIcon className="w-4 h-4" data-id="element-875" />
                  </button>
                </div>) : <p className="text-sm text-gray-500 italic" data-id="element-876">
                No preferred universities selected
              </p>}
          </div>
          <p className="text-xs text-gray-500 mt-2" data-id="element-877">
            If no universities are selected, the scholarship will be available
            for all eligible institutions
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4" data-id="element-878">
          <div className="flex items-start" data-id="element-879">
            <InfoIcon className="w-5 h-5 text-blue-500 mr-3 mt-0.5" data-id="element-880" />
            <div data-id="element-881">
              <h3 className="text-sm font-medium text-blue-800 mb-1" data-id="element-882">
                International Programs
              </h3>
              <p className="text-sm text-blue-700" data-id="element-883">
                For international programs not in the UAE database, MoHESR will
                review the program details during the approval process. Please
                provide accurate information to expedite the review.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Step2ProgramsUniversities;